import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";

import { api } from "../../../api";
import { extractCenterId } from "../../../utils/center";
import { TaskRow } from "./TaskRow";
import { DockMapsLink } from "./DockMapsLink";
import "../css/SimulationPage.css";

/*
 * Vue « simulation de dispatch » : rejeu d'une journée passée par la lib (tournées figées au fil de
 * l'eau) comparé au dispatch réellement effectué. On choisit une simulation, on voit le comparatif
 * simulé vs réel (coût / retard / avance) et le détail des tournées simulées.
 */
const r1 = (x) => Math.round((x || 0) * 10) / 10;
const r2 = (x) => Math.round((x || 0) * 100) / 100;
// Heure locale (Paris) d'un ISO horodaté — sent_at est stocké en UTC.
const hm = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
// Fenêtre horaire compacte : « 14:30→16:41 », ou la valeur seule quand les bornes coïncident.
const hRange = (a, b) => {
  if (!a || !b) { return b || a; }
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return lo !== hi ? `${lo}→${hi}` : lo;
};
// « HH:mm » → minutes du jour.
const hToMin = (s) => {
  if (!s || s.indexOf(":") < 0) { return null; }
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};
// Une échéance d'une tournée : fenêtre (borne basse → borne haute) + heure planifiée (au plus tard).
// L'écart n'apparaît QUE si l'heure planifiée sort de la fenêtre : avant la borne basse = avance
// (−, vert), après la borne haute = retard (+, rouge). Dans la fenêtre = à l'heure, pas d'écart.
function Fourchette({ label, earliest, latest }) {
  const e = hToMin(earliest);
  const l = hToMin(latest);
  let delta = null;
  if (e !== null && l !== null) {
    const lo = Math.min(e, l);
    const hi = Math.max(e, l);
    const planned = l; // heure planifiée = au plus tard
    if (planned > hi) { delta = { min: planned - hi, late: true }; }
    else if (planned < lo) { delta = { min: lo - planned, late: false }; }
  }
  return (
    <span>
      {label} <b>{hRange(earliest, latest)}</b> ({latest}
      {delta && (delta.late
        ? <span style={{ color: "#c0392b" }}>, +{delta.min}min</span>
        : <span style={{ color: "#2c7a4b" }}>, −{delta.min}min</span>)})
    </span>
  );
}
Fourchette.propTypes = { label: PropTypes.string, earliest: PropTypes.string, latest: PropTypes.string };
const simLabel = (s) => {
  // Tolérances affichées seulement si elles s'écartent des défauts (15/30/15), pour ne pas alourdir.
  const tol = (s.max_delay_min !== 15 || s.max_wait_min !== 30 || s.urgent_slack_min !== 15)
    ? ` · tol ${s.max_delay_min}/${s.max_wait_min}/${s.urgent_slack_min}` : "";
  // Capacité affichée seulement si elle s'écarte des défauts (scooter 16/80, voiture 40/250).
  const cap = (s.max_bags_paris !== 16 || s.max_weight_paris !== 80 || s.max_bags_other !== 40 || s.max_weight_other !== 250)
    ? ` · cap ${s.max_bags_paris}/${s.max_weight_paris}·${s.max_bags_other}/${s.max_weight_other}` : "";
  // Montant visé par zone : affiché seulement s'il a été activé (défaut : aucun objectif).
  const miniParis = s.min_tour_eur_paris, miniOther = s.min_tour_eur_other;
  const mini = miniParis == null && miniOther == null
    ? ""
    : ` · visé ${miniParis == null ? "—" : `${miniParis}€`}/${miniOther == null ? "—" : `${miniOther}€`}`;
  return `${s.day} · pas ${s.step_min} min${tol}${cap}${mini} · ${new Date(s.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`;
};

// Cellule chiffrée : « — » si la valeur manque (ex. real_lib absent d'une vieille simulation).
const cell = (x, unit) => (x === undefined || x === null ? "—" : `${r2(x)}${unit}`);

// Une ligne du comparatif à 3 colonnes (réel payé / réel recalculé par la lib / simulé) + deux écarts :
//   Δ méthode = réel-lib − réel payé (effet de la formule de coût, à tournées identiques)
//   Δ dispatch = simulé − réel-lib (effet du découpage, à formule identique) → négatif = la lib améliore.
// `good` = sens d'un écart favorable (« low » : moins c'est mieux). null = pas de jugement (commandes).
function CompareRow({ label, title, paid, lib, sim, unit, good }) {
  // Les deux écarts se lisent « après − avant » : négatif = l'étape a réduit la métrique (favorable
  // pour coût / retard / attente). Δ méthode = réel-lib − réel payé ; Δ dispatch = simulé − réel-lib.
  const dMethod = paid != null && lib != null ? lib - paid : null;
  const dDispatch = lib != null && sim != null ? sim - lib : null;
  // Règle simple et prévisible : tout écart AFFICHÉ non nul porte sa couleur. Un seuil absolu
  // (l'ancien 0,05) convenait aux euros et aux minutes mais aveuglait les ratios — sur un coût au
  // km d'environ 1 €, un écart de 0,03 € vaut 3 % et doit se voir.
  const cls = (delta) => {
    if (delta === null || !good || r2(delta) === 0) return "";
    return (good === "low" ? delta < 0 : delta > 0) ? "Sim-good" : "Sim-bad";
  };
  // Écart en absolu + % relatif à la base (réel payé pour le Δ méthode, réel-lib pour le Δ
  // dispatch). Le % n'est montré qu'à partir de 1 % : en dessous, « (0%) » n'apprend rien.
  const signed = (d, base) => {
    if (d === null) return "—";
    const abs = `${d > 0 ? "+" : ""}${r2(d)}${unit}`;
    if (!base || Math.abs(base) < 0.001) return abs;
    const pct = Math.round((d / base) * 100);
    return pct === 0 ? abs : `${abs} (${pct > 0 ? "+" : ""}${pct}%)`;
  };
  return (
    <tr>
      <td title={title || undefined}>{label}</td>
      <td className="Sim-num">{cell(paid, unit)}</td>
      <td className="Sim-num">{cell(lib, unit)}</td>
      <td className="Sim-num">{cell(sim, unit)}</td>
      <td className={`Sim-num ${cls(dMethod)}`}>{signed(dMethod, paid)}</td>
      <td className={`Sim-num ${cls(dDispatch)}`}>{signed(dDispatch, lib)}</td>
    </tr>
  );
}
CompareRow.propTypes = {
  label: PropTypes.string, title: PropTypes.string, paid: PropTypes.number, lib: PropTypes.number,
  sim: PropTypes.number, unit: PropTypes.string, good: PropTypes.string,
};

// Statistiques par tournée d'une colonne (moyenne, min, max), calculées par le back.
const st = (col) => (col && col.stats) || {};

// Complète la colonne « réel payé » avec la distance du réel-lib (mêmes tournées, même route) et
// les ratios qui en découlent, quand la simulation est antérieure à leur calcul côté back.
function withPaidDistance(paid, lib) {
  const ps = st(paid), ls = st(lib);
  if (!paid || ps.distance_km_total || !ls.distance_km_total) { return paid; }
  const km = ls.distance_km_total;
  const hours = paid.elapsed_min ? paid.elapsed_min / 60 : null;
  const deliveringHours = paid.delivering_min ? paid.delivering_min / 60 : null;
  const r2v = (v) => (v == null ? null : Math.round(v * 100) / 100);
  return {
    ...paid,
    distance_km: km,
    stats: {
      ...ps,
      distance_km_total: km,
      distance_km: ls.distance_km,          // mêmes tournées : même étendue
      speed_kmh: { ...(ps.speed_kmh || {}), avg: r2v(deliveringHours ? km / deliveringHours : null) },
      cost_per_km: { ...(ps.cost_per_km || {}), avg: r2v(paid.cost_eur ? paid.cost_eur / km : null) },
      cost_per_hour: {
        ...(ps.cost_per_hour || {}),
        avg: r2v(hours && paid.cost_eur ? paid.cost_eur / hours : null),
      },
    },
  };
}

// Ligne « moyenne (min – max) » : la moyenne alimente les colonnes et les Δ, l'étendue est affichée
// dessous en petit — une même moyenne peut cacher des dispersions très différentes.
function StatRow({ label, title, metric, unit, paid, lib, sim, good = "low" }) {
  const range = (col) => {
    const m = st(col)[metric];
    if (!m || m.min == null) return null;
    return `${r2(m.min)} – ${r2(m.max)}`;
  };
  const avg = (col) => (st(col)[metric] || {}).avg;
  return (
    <>
      <CompareRow label={label} title={title} unit={unit} good={good}
        paid={avg(paid)} lib={avg(lib)} sim={avg(sim)} />
      <tr className="Sim-range">
        <td className="Sim-range-label">étendue</td>
        <td className="Sim-num">{range(paid) || ""}</td>
        <td className="Sim-num">{range(lib) || ""}</td>
        <td className="Sim-num">{range(sim) || ""}</td>
        <td /><td />
      </tr>
    </>
  );
}
StatRow.propTypes = {
  label: PropTypes.string, title: PropTypes.string, metric: PropTypes.string, unit: PropTypes.string,
  paid: PropTypes.object, lib: PropTypes.object, sim: PropTypes.object, good: PropTypes.string,
};

// Les tournées du réel payé face aux contraintes du simulé (capacités, retard toléré, avance
// tolérée) : une partie de l'écart de volume avec le simulé s'explique là. Sacs et poids sont
// strictement comparables ; retard et avance sont ceux CONSTATÉS par livraison (la règle du
// simulé porte, elle, sur le retard ajouté par le regroupement) — indicatif.
function RealExcesses({ data }) {
  const caps = data.caps || {};
  const fmt = (z, k, unit) => (caps[z] && caps[z][k] != null ? `${caps[z][k]}${unit}` : "?");
  const items = [
    { key: "bags", label: "sacs > capacité", hint: `scooter ${fmt("paris", "bags", "")} · voiture ${fmt("other", "bags", "")}` },
    { key: "weight", label: "poids > capacité", hint: `scooter ${fmt("paris", "weight", " kg")} · voiture ${fmt("other", "weight", " kg")}` },
    { key: "delay", label: "retard > toléré", hint: data.max_delay_min != null ? `une livraison > ${data.max_delay_min} min de retard` : "—" },
    { key: "wait", label: "avance > tolérée", hint: data.max_wait_min != null ? `une livraison > ${data.max_wait_min} min avant l’ouverture` : "illimitée" },
  ];
  const total = data.tours || 0;
  const pct = (n) => (total ? `${Math.round((100 * n) / total)} %` : "—");
  const ok = data.compliant || { count: 0, labels: [] };
  return (
    <div className="Sim-excess">
      <h4>
        Réel payé vs contraintes du simulé — <b>{total}</b> tournées,{" "}
        <span className="Sim-good" title={ok.labels.join(", ")}>
          <b>{ok.count}</b> respectent tout ({pct(ok.count)})
        </span>
        {" · "}
        <span className={total - ok.count ? "Sim-bad" : ""}>
          <b>{total - ok.count}</b> hors d’au moins une ({pct(total - ok.count)})
        </span>
      </h4>
      <div className="Sim-excess-row">
        {items.map((it) => {
          const x = data[it.key] || { count: 0, labels: [] };
          const title = `${it.hint}${x.labels.length ? "\n" + x.labels.join(", ") : ""}`;
          return (
            <span key={it.key} className={`Sim-excess-item ${x.count ? "Sim-bad" : ""}`} title={title}>
              {it.label} <b>{x.count}</b> / {total} ({pct(x.count)})
            </span>
          );
        })}
      </div>
    </div>
  );
}
RealExcesses.propTypes = { data: PropTypes.object };

// Tableau comparatif réel payé / réel-lib / simulé (5 métriques). Global et sous-total Paris.
// `costRate` (optionnel) = coût recalculé au taux horaire net moyen (neutralise le barème / le livreur) :
// {paid, lib, sim, label}. Ligne ajoutée sous le coût net livreur.
function CompareTable({ title, paid, lib, sim, costRate, excesses }) {
  // Les lignes de détail (kilométrage, vitesse, ratios) sont repliées par défaut : le haut du
  // tableau suffit à lire l'essentiel, le reste sert quand on creuse.
  const [showDetails, setShowDetails] = useState(false);
  const l = lib || {};
  // Le réel payé n'a pas de compteur kilométrique : ce sont les MÊMES tournées que le réel-lib,
  // donc la même route. Les simulations générées avant que le back ne le fasse lui-même sont
  // complétées ici : distance reprise telle quelle, ratios recalculés avec les temps et coûts
  // PAYÉS. Les étendues qui dépendent du temps payé (vitesse, €/km) ne sont pas reconstituables
  // sans le détail par tournée — elles apparaîtront à la régénération de la simulation.
  const paidCol = withPaidDistance(paid, l);
  // Statistiques par tournée de la colonne (moyenne / min / max), calculées par le back.
  return (
    <div className="Sim-compare">
      <h3>
        {title}
        <button type="button" className="Sim-toggle" onClick={() => setShowDetails(!showDetails)}>
          {showDetails ? "− détails" : "+ détails (km, vitesse, ratios)"}
        </button>
      </h3>
      <table>
        <thead>
          <tr>
            <th />
            <th className="Sim-num">Réel payé</th>
            <th className="Sim-num" title="Mêmes tournées que le réel, coût/retard/avance recalculés par notre lib.">Réel (lib)</th>
            <th className="Sim-num">Simulé</th>
            <th className="Sim-num" title="Réel (lib) − réel payé : effet de la méthode de calcul (barème payé vs estimation lib), à tournées identiques.">Δ méthode</th>
            <th className="Sim-num" title="Simulé − réel-lib : effet du re-découpage, à méthode de calcul identique. Négatif (vert) = le simulé réduit la métrique (coût / retard / attente).">Δ dispatch</th>
          </tr>
        </thead>
        <tbody>
          <CompareRow label="Tournées" paid={paid.dock_count} lib={l.dock_count} sim={sim.dock_count} unit="" good="low" />
          <CompareRow label="Commandes" paid={paid.order_count} lib={l.order_count} sim={sim.order_count} unit="" />
          <CompareRow label="Coût (net livreur)" paid={paid.cost_eur} lib={l.cost_eur} sim={sim.cost_eur} unit=" €" good="low" />
          {costRate && (
            <>
              <CompareRow
                label="Heures mobilisées"
                title="Réel payé = heures réellement travaillées (done − gone : trafic, stationnement, aléas). Réel-lib = ce que la lib estime pour CES MÊMES tournées (routage optimal + service standard). Simulé = même estimation, sur les tournées de la lib. C'est cette différence d'heures — pas le taux, identique partout — qui explique l'écart de coût ci-dessous."
                paid={costRate.hoursPaid} lib={costRate.hoursLib} sim={costRate.hoursSim} unit=" h" good="low"
              />
              <CompareRow
                label={`Coût @ taux moyen${costRate.label ? ` (${costRate.label})` : ""}`}
                title="Heures ci-dessus × taux horaire net moyen par véhicule (scooter à Paris, voiture en banlieue), calé sur le réel : Σ gain_real / Σ heures réelles. Le taux étant le même partout, l'écart ne vient QUE des heures. Δ méthode = écart théorie/exécution (la lib estime des tournées plus rapides que la réalité) — ce n'est PAS une économie, ce sont les mêmes tournées. Δ dispatch = le seul effet du découpage, à modèle de temps identique."
                paid={costRate.paid} lib={costRate.lib} sim={costRate.sim} unit=" €" good="low"
              />
            </>
          )}
          <CompareRow
            label="Retard"
            title="Réel payé = retard réellement subi. Réel-lib = retard que la lib PRÉDIT pour ces mêmes tournées, chacune partant à son heure de départ réelle. Simulé = même prédiction, sur les tournées de la lib. Δ méthode = écart théorie/exécution (trafic et aléas que la lib n'anticipe pas) ; Δ dispatch = effet du seul re-découpage — prédiction contre prédiction, donc ISO."
            paid={paid.delay_min} lib={l.delay_min} sim={sim.delay_min} unit=" min" good="low"
          />
          <CompareRow
            label="Avance"
            title="Réel payé = livraisons réellement faites avant l'ouverture du créneau. Réel-lib = avance que la lib PRÉDIT pour ces mêmes tournées (départ réel). Simulé = attente planifiée sur les tournées de la lib. Δ dispatch se lit prédiction contre prédiction."
            paid={paid.wait_min} lib={l.wait_min} sim={sim.wait_min} unit=" min" good="low"
          />
          <CompareRow
            label="Chargement"
            title="Total du temps de chargement des sacs avant départ. Payé = réel (orderstatdriver.time_loading) ; réel-lib & simulé = estimation de la lib."
            paid={paid.loading_min} lib={l.loading_min} sim={sim.loading_min} unit=" min" good="low"
          />
          <CompareRow
            label="Livraison (trajet+service)"
            title="Trajet + service (coup de frein) cumulés. Réel payé = time_delivering réel (trajet et service fusionnés, non dissociables) ; réel-lib & simulé = trajet + coup de frein estimés par la lib. C'est la seule brique livraison comparable sur les 3 colonnes."
            paid={paid.delivering_min} lib={l.delivering_min} sim={sim.delivering_min} unit=" min" good="low"
          />
          {showDetails && (
            <>
          <CompareRow
            label="Km parcourus"
            title="Distance de route OSRM cumulée, aller (dépôt → 1er point) et retour (dernier point → dépôt) inclus. Réel payé & réel-lib : mêmes tournées, donc même route physique — le réel payé n'a pas de compteur kilométrique propre."
            paid={st(paidCol).distance_km_total} lib={st(l).distance_km_total} sim={st(sim).distance_km_total} unit=" km" good="low"
          />
          <StatRow label="Km / tournée" metric="distance_km" unit=" km" paid={paidCol} lib={l} sim={sim}
            title="Longueur moyenne d'une tournée, et l'étendue min–max. Aller et retour au dépôt inclus." />
          <StatRow label="Temps / tournée" metric="time_min" unit=" min" paid={paidCol} lib={l} sim={sim}
            title="Durée moyenne d'une tournée. Réel payé = heures réellement mobilisées (done − gone) ; réel-lib & simulé = temps estimé (chargement + trajet + coup de frein)." />
          <StatRow label="Vitesse" metric="speed_kmh" unit=" km/h" paid={paidCol} lib={l} sim={sim}
            title="Vitesse commerciale : km ÷ temps de livraison (trajet + arrêts). Seule base comparable aux trois colonnes, le réel payé ne dissociant pas trajet et service." />
          <StatRow label="Coût / tournée" metric="cost_eur" unit=" €" paid={paidCol} lib={l} sim={sim}
            title="Coût net livreur moyen d'une tournée, et l'étendue min–max." />
          <StatRow label="Coût / km" metric="cost_per_km" unit=" €/km" paid={paidCol} lib={l} sim={sim}
            title="Moyenne des €/km DES TOURNÉES (pas coût total ÷ km total) : une petite tournée chère pèse autant qu'une grande." />
          <StatRow label="Coût / heure" metric="cost_per_hour" unit=" €/h" paid={paidCol} lib={l} sim={sim}
            title="Moyenne des €/heure des tournées, sur le temps de la colonne (réel mobilisé, ou estimé)." />
            </>
          )}
        </tbody>
      </table>
      {excesses && <RealExcesses data={excesses} />}
    </div>
  );
}
CompareTable.propTypes = {
  title: PropTypes.string, paid: PropTypes.object, lib: PropTypes.object, sim: PropTypes.object,
  costRate: PropTypes.object, excesses: PropTypes.object,
};

// Valeur d'une tournée suivie de son écart à la moyenne des tournées simulées : +18 % en rouge
// (au-dessus de la moyenne, donc plus cher / plus long), −12 % en vert. Rien si pas de moyenne.
function Gap({ value, avg, unit, digits = 2 }) {
  const shown = digits === 0 ? Math.round(value) : (digits === 1 ? r1(value) : r2(value));
  if (!avg) return <>{shown}{unit}</>;
  const pct = Math.round(((value - avg) / avg) * 100);
  return (
    <>
      {shown}{unit}
      <span className={pct > 0 ? "Sim-gap-up" : "Sim-gap-down"}>
        {" "}{pct > 0 ? "+" : ""}{pct}%
      </span>
    </>
  );
}
Gap.propTypes = { value: PropTypes.number, avg: PropTypes.number, unit: PropTypes.string, digits: PropTypes.number };

// Récapitulatif du gain minimum visé : combien de tournées l'atteignent, combien rapportent moins,
// et lesquelles — avec un lien vers chacune. Jugé sur le VERSÉ (minimum garanti du barème inclus) :
// le montant visé est un plancher de gain livreur, il se compare à ce que le livreur touche.
function TargetRecap({ docks, targetParis, targetOther }) {
  const targetOf = (dock) => (dock.zone === "paris" ? targetParis : targetOther);
  const judged = docks.filter((d) => targetOf(d) && d.cost_eur != null);
  if (!judged.length) { return null; }
  const below = judged.filter((d) => d.cost_eur < targetOf(d));
  const label = targetParis === targetOther
    ? `${targetParis} €`
    : `${targetParis == null ? "—" : `${targetParis} €`} scooter · ${targetOther == null ? "—" : `${targetOther} €`} voiture`;
  return (
    <div className={below.length ? "Sim-target Sim-target--miss" : "Sim-target"}>
      <b>Gain minimum visé {label}</b> :{" "}
      <b>{judged.length - below.length}</b> tournée{judged.length - below.length > 1 ? "s" : ""} l’atteignent,{" "}
      <b>{below.length}</b> en dessous
      {below.length > 0 && (
        <>
          {" — "}
          {below
            .slice()
            .sort((a, b) => a.cost_eur - b.cost_eur)
            .map((d, i) => (
              <span key={d.label}>
                {i > 0 && ", "}
                <a href={`#dock-${d.label}`} className="Sim-target-link">
                  {d.label}
                </a>
                {" "}({r2(d.cost_eur)} €)
              </span>
            ))}
        </>
      )}
    </div>
  );
}
TargetRecap.propTypes = {
  docks: PropTypes.array, targetParis: PropTypes.number, targetOther: PropTypes.number,
};

// Repère de coût : ce que cette tournée coûte face à la somme des coûts moyens d'une livraison,
// par arrondissement + heure de créneau, sur le réel payé — même jour de semaine (×4) et 14 jours.
// En dessous de la référence = mieux groupé que la moyenne (vert), au-dessus = moins bien (rouge).
function CostRef({ dock }) {
  const wd = dock.cost_ref_weekday, d14 = dock.cost_ref_14d;
  if (!wd && !d14) { return null; }
  const cell = (b) => {
    if (!b || !b.covered) { return <span className="Sim-ref-na">—</span>; }
    const cls = dock.cost_eur < b.eur ? "Sim-good" : (dock.cost_eur > b.eur ? "Sim-bad" : "");
    const partial = b.covered < b.total ? ` (${b.covered}/${b.total} cmd connues)` : "";
    return <span className={cls} title={`Somme des coûts moyens d'une livraison (arrondissement + heure), réel payé${partial}`}>{r2(b.eur)} €</span>;
  };
  return (
    <span className="Sim-line Sim-ref" title="Repère : cette tournée face au coût moyen réel des mêmes livraisons (par arrondissement et créneau).">
      repère réel : tournée <b>{r2(dock.cost_eur)} €</b> · même jour {cell(wd)} · 14 j {cell(d14)}
    </span>
  );
}
CostRef.propTypes = { dock: PropTypes.object };

function SimDock({ dock, tasks, depot, avg = {} }) {
  return (
    <div className={`Sim-dock Sim-dock--${dock.zone}`} id={`dock-${dock.label}`}>
      <div className="Sim-dock-head">
        <b>{dock.label}</b>
        <span className="Sim-zone">{dock.zone === "paris" ? "Paris" : "Banlieue"}</span>
        <span className="Sim-sent">verrouillée {hm(dock.sent_at)}</span>
        <DockMapsLink orders={dock.orders} tasks={tasks} depot={depot} avoidHighways={dock.zone === "paris"} />
        <span className="Sim-lines">
          <span className="Sim-line">
            {dock.n_orders} cmd · {dock.bags} sacs · {r1(dock.weight_kg)} kg
          </span>
          <span
            className="Sim-line"
            title="Durée totale = chargement + trajet + coup de frein. L'attente client (arrivé avant l'ouverture du créneau) est en plus, hors total. Distances et durées incluent l'aller (dépôt → 1er point) et le retour (dernier point → dépôt) ; les % situent la tournée face à la moyenne des tournées simulées."
          >
            <Gap value={dock.time_min} avg={avg.time_min} unit="m" digits={1} />
            {" (charg. "}{r1(dock.loading_min)} + trajet {r1(dock.travel_min)} + coup de frein {r1(dock.brake_min)} + attente {r1(dock.wait_min)})
            {dock.distance_km > 0 && (
              <>{" · "}<Gap value={dock.distance_km} avg={avg.distance_km} unit=" km" digits={1} /></>
            )}
            {dock.distance_km > 0 && (dock.travel_min || 0) + (dock.brake_min || 0) > 0 && (
              <>
                {" · "}
                <Gap
                  value={dock.distance_km / (((dock.travel_min || 0) + (dock.brake_min || 0)) / 60)}
                  avg={avg.speed_kmh}
                  unit=" km/h"
                  digits={1}
                />
              </>
            )}
            {dock.delay_min > 0 && <span className="Sim-late"> · retard {r1(dock.delay_min)}m</span>}
            {dock.wait_min > 0 && <span className="Sim-early"> · avance {r1(dock.wait_min)}m</span>}
          </span>
          <span className="Sim-line" title="Coût de la tournée et ses ratios, avec l'écart à la moyenne des tournées simulées.">
            {r2(dock.cost_eur)} €
            {dock.distance_km > 0 && (
              <>{" · "}<Gap value={dock.cost_eur / dock.distance_km} avg={avg.cost_per_km} unit=" €/km" digits={2} /></>
            )}
            {dock.time_min > 0 && (
              <>{" · "}<Gap value={dock.cost_eur / (dock.time_min / 60)} avg={avg.cost_per_hour} unit=" €/h" digits={2} /></>
            )}
          </span>
          <CostRef dock={dock} />
        </span>
      </div>
      {dock.targets && dock.targets.depart && (
        <div className="Sim-targets" title="Fenêtre d'action : au plus tôt (partir maintenant) → au plus tard (dernier départ sans retard). Retour = dernière commande → entrepôt inclus.">
          <span className="Sim-targets-tag">fenêtre</span>
          <Fourchette label="chargement" earliest={dock.targets.prep_earliest} latest={dock.targets.prep} />
          {" · "}<Fourchette label="départ" earliest={dock.targets.depart_earliest} latest={dock.targets.depart} />
          {" · "}<Fourchette label="retour" earliest={dock.targets.return_earliest} latest={dock.targets.return} />
        </div>
      )}
      <table className="Sim-orders">
        <tbody>
          {(dock.orders || []).map((o, i) => <TaskRow key={`${o.ref}-${i}`} order={o} tasks={tasks} />)}
        </tbody>
      </table>
    </div>
  );
}
SimDock.propTypes = {
  avg: PropTypes.object, dock: PropTypes.object, tasks: PropTypes.object, depot: PropTypes.object };

function SimulationPage({ centerId }) {
  const location = useLocation();
  const history = useHistory();
  const [sims, setSims] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [parisOnly, setParisOnly] = useState(true);  // par défaut : focus tournées intramuros
  const [dayOnly, setDayOnly] = useState(true);  // par défaut : ignorer veille/surlendemain
  const [copied, setCopied] = useState(false);
  // Formulaire « créer une simulation » (défaut : la veille, pas 30 min, centre courant).
  const [newDay, setNewDay] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [newStep, setNewStep] = useState(30);
  const [newCenter, setNewCenter] = useState(centerId || "");
  // Tolérances de dispatch (mêmes défauts que le dry-run) : comparaison équitable.
  const [newMaxDelay, setNewMaxDelay] = useState(15);
  const [newMaxWait, setNewMaxWait] = useState(30);
  const [newUrgentSlack, setNewUrgentSlack] = useState(15);
  // Plancher de rémunération par tournée (€) : en deçà, on ne mobilise pas un livreur. Désactivable.
  const [minTourOn, setMinTourOn] = useState(true);  // gain minimum scooter actif par défaut (config lib : 18 €)
  const [newMinTourParis, setNewMinTourParis] = useState(18);
  const [newMinTourOther, setNewMinTourOther] = useState(18);
  // Capacité par zone (mêmes défauts que le dry-run) : scooter Paris 16/80, voiture banlieue 40/250.
  const [newBagsParis, setNewBagsParis] = useState(16);
  const [newWeightParis, setNewWeightParis] = useState(80);
  const [newBagsOther, setNewBagsOther] = useState(40);
  const [newWeightOther, setNewWeightOther] = useState(250);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState("");

  const refresh = () => {
    if (centerId) { api.listSimulations(centerId).then(setSims); }
  };
  useEffect(refresh, [centerId]);

  const load = (id) => {
    setSelectedId(id);
    // La sim sélectionnée est reflétée dans l'URL (?sim=…) → lien partageable tel quel.
    const params = new URLSearchParams(history.location.search);
    if (id) { params.set("sim", id); } else { params.delete("sim"); }
    history.replace({ pathname: history.location.pathname, search: params.toString() });
    if (!id) { setData(null); return; }
    setLoading(true);
    api.getSimulation(id).then((d) => { setData(d); setLoading(false); });
  };

  // Ouverture d'un lien partagé : pré-sélectionne la sim indiquée dans l'URL.
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get("sim");
    if (fromUrl) { load(fromUrl); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le centre du formulaire suit le centre courant tant que l'utilisateur n'y a pas touché.
  useEffect(() => { if (centerId) { setNewCenter(centerId); } }, [centerId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Sonde la liste jusqu'à ce que la sim (jour, pas) apparaisse pour ce centre, puis la sélectionne.
  // Borne à ~14 min (la génération dure ~10 min) pour ne pas sonder indéfiniment en cas d'échec du job.
  const pollForSim = (center, day, step, tol, attempt = 0) => {
    api.listSimulations(center).then((list) => {
      setSims(list);
      const found = list.find((s) => s.day === day && s.step_min === step
        && s.max_delay_min === tol.max_delay_min && s.max_wait_min === tol.max_wait_min
        && s.urgent_slack_min === tol.urgent_slack_min
        && s.max_bags_paris === tol.max_bags_paris && s.max_weight_paris === tol.max_weight_paris
        && s.max_bags_other === tol.max_bags_other && s.max_weight_other === tol.max_weight_other);
      if (found) { setGenerating(false); setGenMsg(""); load(found.id); return; }
      if (attempt >= 42) { setGenerating(false); setGenMsg("Toujours pas prête — réessaie « Actualiser » dans un moment."); return; }
      setTimeout(() => pollForSim(center, day, step, tol, attempt + 1), 20000);
    }).catch(() => setTimeout(() => pollForSim(center, day, step, tol, attempt + 1), 20000));
  };

  // Suit la progression du job (0-100 %) via son id, puis charge la sim finie. Retombe sur la sonde de
  // liste si le job est inconnu/expiré ou si aucun id n'est fourni.
  const pollProgress = (center, day, step, tol, jobId, attempt = 0) => {
    if (!jobId) { pollForSim(center, day, step, tol); return; }
    api.simulationStatus(jobId).then((st) => {
      if (st.status === "finished" && st.simulation_id) {
        setGenerating(false); setGenMsg(""); refresh(); load(String(st.simulation_id)); return;
      }
      if (st.status === "failed") { setGenerating(false); setGenMsg("Échec de la génération."); return; }
      if (st.status === "unknown") { pollForSim(center, day, step, tol); return; }  // filet : sonde la liste
      setGenMsg(`Génération en cours ${st.progress || 0}%`);
      if (attempt >= 300) { pollForSim(center, day, step, tol); return; }  // ~15 min max
      setTimeout(() => pollProgress(center, day, step, tol, jobId, attempt + 1), 3000);
    }).catch(() => setTimeout(() => pollProgress(center, day, step, tol, jobId, attempt + 1), 5000));
  };

  const createSim = () => {
    const center = parseInt(newCenter, 10);
    const step = parseInt(newStep, 10);
    if (!center || !newDay || !step) { setGenMsg("Renseigne centre, jour et pas."); return; }
    const tol = {
      max_delay_min: parseInt(newMaxDelay, 10),
      max_wait_min: parseInt(newMaxWait, 10),
      urgent_slack_min: parseInt(newUrgentSlack, 10),
      min_tour_eur_paris: minTourOn ? parseFloat(newMinTourParis) : null,
      min_tour_eur_other: minTourOn ? parseFloat(newMinTourOther) : null,
      max_bags_paris: parseInt(newBagsParis, 10),
      max_weight_paris: parseFloat(newWeightParis),
      max_bags_other: parseInt(newBagsOther, 10),
      max_weight_other: parseFloat(newWeightOther),
    };
    setGenerating(true);
    setGenMsg("Lancement…");
    api.createSimulation(center, newDay, step, tol).then((res) => {
      if (res && res.status === "exists") {
        const ok = window.confirm(
          `Une simulation ${newDay} · pas ${step} min · tolérances ${tol.max_delay_min}/${tol.max_wait_min}/${tol.urgent_slack_min} · capacité ${tol.max_bags_paris}s/${tol.max_weight_paris}kg · ${tol.max_bags_other}s/${tol.max_weight_other}kg existe déjà.\nL'écraser et la régénérer ?`
        );
        if (!ok) {  // non : on sélectionne l'existante
          refresh(); load(res.id); setGenerating(false); setGenMsg("Simulation existante sélectionnée.");
          return;
        }
        setGenMsg("Écrasement + génération en cours…");
        api.createSimulation(center, newDay, step, tol, true)
          .then((res2) => pollProgress(center, newDay, step, tol, res2 && res2.job_id))
          .catch(() => { setGenerating(false); setGenMsg("Échec de l'écrasement."); });
        return;
      }
      setGenMsg("Génération en cours…");
      pollProgress(center, newDay, step, tol, res && res.job_id);
    }).catch(() => { setGenerating(false); setGenMsg("Échec du lancement."); });
  };

  return (
    <div className="Sim-page">
      <div className="Sim-controls">
        <fieldset className="Sim-fieldset">
          <legend>Choisir une simulation</legend>
          <select value={selectedId} onChange={(e) => load(e.target.value)}>
            <option value="">— choisir —</option>
            {sims.map((s) => <option key={s.id} value={s.id}>{simLabel(s)}</option>)}
          </select>
          <button className="Sim-refresh" onClick={refresh}>Actualiser</button>
          {selectedId && (
            <button className="Sim-copy" onClick={copyLink} title="Copier le lien de cette simulation (partageable).">
              {copied ? "Lien copié ✓" : "Copier le lien"}
            </button>
          )}
          <button
            className="Sim-copy"
            onClick={() => history.push("/dispatch-simulation-chart")}
            title="Courbes des coûts (réel payé / réel-lib / simulé) de toutes les simulations du centre, en ordre chronologique."
          >
            Courbes des coûts
          </button>
        </fieldset>

        <fieldset className="Sim-fieldset">
          <legend>Créer une simulation</legend>
          <label>Jour <input type="date" value={newDay} onChange={(e) => setNewDay(e.target.value)} /></label>
          <label>
            Pas
            <select value={newStep} onChange={(e) => setNewStep(parseInt(e.target.value, 10))}>
              <option value={30}>30 min</option>
              <option value={15}>15 min</option>
              <option value={5}>5 min</option>
            </select>
          </label>
          <label>Centre <input type="number" className="Sim-center-input" value={newCenter} onChange={(e) => setNewCenter(e.target.value)} /></label>
          <label title="Retard ajouté toléré au regroupement (min) — même levier que le dry-run.">Retard toléré <input type="number" min="0" className="Sim-tol-input" value={newMaxDelay} onChange={(e) => setNewMaxDelay(e.target.value)} /></label>
          <label title="Attente max par livraison (min) — même levier que le dry-run.">Attente max <input type="number" min="0" className="Sim-tol-input" value={newMaxWait} onChange={(e) => setNewMaxWait(e.target.value)} /></label>
          <label title="Préavis d'urgence des étiquettes (min) — même levier que le dry-run.">Urgent si &lt; <input type="number" min="0" className="Sim-tol-input" value={newUrgentSlack} onChange={(e) => setNewUrgentSlack(e.target.value)} /></label>
          <label title="Gain minimum visé par tournée : ce qu'on souhaite que le livreur gagne au minimum. Comparé au versé (minimum garanti du barème inclus) ; pousse la construction à densifier les tournées qui rapportent moins. Deux valeurs car une tournée scooter coûte moins cher qu'une tournée voiture.">
            <input type="checkbox" checked={minTourOn} onChange={(e) => setMinTourOn(e.target.checked)} /> Tournée visée
            <input type="number" min="0" step="0.5" className="Sim-tol-input" value={newMinTourParis} disabled={!minTourOn} onChange={(e) => setNewMinTourParis(e.target.value)} title="Scooter Paris" /> €&nbsp;scooter
            <input type="number" min="0" step="0.5" className="Sim-tol-input" value={newMinTourOther} disabled={!minTourOn} onChange={(e) => setNewMinTourOther(e.target.value)} title="Voiture banlieue" /> €&nbsp;voiture
          </label>
          <label title="Capacité scooter Paris intramuros (sacs).">Scooter sacs <input type="number" min="1" className="Sim-tol-input" value={newBagsParis} onChange={(e) => setNewBagsParis(e.target.value)} /></label>
          <label title="Capacité scooter Paris intramuros (kg).">kg <input type="number" min="1" className="Sim-tol-input" value={newWeightParis} onChange={(e) => setNewWeightParis(e.target.value)} /></label>
          <label title="Capacité voiture banlieue (sacs).">Voiture sacs <input type="number" min="1" className="Sim-tol-input" value={newBagsOther} onChange={(e) => setNewBagsOther(e.target.value)} /></label>
          <label title="Capacité voiture banlieue (kg).">kg <input type="number" min="1" className="Sim-tol-input" value={newWeightOther} onChange={(e) => setNewWeightOther(e.target.value)} /></label>
          <button className="Sim-generate" onClick={createSim} disabled={generating}>
            {generating ? "Génération…" : "Générer"}
          </button>
          {genMsg && <span className="Sim-hint">{genMsg}</span>}
        </fieldset>

        <div className="Sim-display-filters">
          <label title="N'afficher que les tournées Paris intramuros (zone scooter).">
            <input type="checkbox" checked={parisOnly} onChange={(e) => setParisOnly(e.target.checked)} /> Paris intramuros seulement
          </label>
          <label title="Ignorer les tournées dont le créneau est la veille ou le surlendemain (livraisons étalées / franchissant minuit).">
            <input type="checkbox" checked={dayOnly} onChange={(e) => setDayOnly(e.target.checked)} /> Journée seule
          </label>
          {loading && <span className="Sim-hint">Chargement…</span>}
        </div>
      </div>

      {!data && !loading && (
        <div className="Sim-empty">
          Choisis une simulation dans la liste, ou crée-en une nouvelle (jour + pas + centre). La
          génération tourne en tâche de fond (~10&nbsp;min) puis s’affiche ici.
        </div>
      )}

      {data && (() => {
        const z = data.zones || {};
        const rl = data.real_lib || {};
        // Les tableaux reflètent la checkbox « Journée seule » : quand elle est cochée, on bascule sur les
        // sous-totaux journée-seule (global → day_only, Paris → paris_day_only). Fallback global si absent.
        const dayTag = dayOnly ? " · journée seule" : "";
        const globalPaid = dayOnly && z.day_only ? z.day_only.real : data.real;
        const globalLib = dayOnly && rl.day_only ? rl.day_only : data.real_lib;
        const globalSim = dayOnly && z.day_only ? z.day_only.sim : data.sim;
        const paris = dayOnly ? z.paris_day_only : z.paris;
        const parisLib = dayOnly ? rl.paris_day_only : rl.paris;
        // Tournées réelles hors contraintes, sur le même périmètre que chaque tableau.
        const ex = data.real_excesses || {};
        // Simulations antérieures : les sous-totaux stockés n'ont pas de distance — l'API la
        // recalcule depuis les tournées stockées, on la réinjecte (hors « journée seule »).
        if (paris && paris.sim && paris.sim.distance_km == null && !dayOnly) {
          paris.sim.distance_km = data.sim.paris_distance_km;
        }
        // Simulation Paris seul (dispatch auto banlieue désactivé) : le comparatif global opposerait
        // des tournées Paris à un dispatch réel qui contient aussi la banlieue. On ne montre que
        // « dont Paris ». Les simulations d'avant ce réglage n'ont pas le champ : global par défaut.
        const simZones = data.simulated_zones || [];
        // Moyennes des tournées simulées : servent d'étalon aux écarts affichés sur chaque tournée.
        const simStats = (data.sim && data.sim.stats) || {};
        const simAvg = {
          distance_km: (simStats.distance_km || {}).avg,
          time_min: (simStats.time_min || {}).avg,
          cost_per_km: (simStats.cost_per_km || {}).avg,
          cost_per_hour: (simStats.cost_per_hour || {}).avg,
          speed_kmh: (simStats.speed_kmh || {}).avg,
        };
        const parisOnlySim = simZones.length > 0 && !simZones.includes("other");
        // Réel-lib dégradé : OSRM n'a pas su router une part notable des commandes (matrice incomplète,
        // tuiles /table en échec). Au-delà de 5 % on prévient : la colonne « Réel (lib) » sous-estime.
        const rlUnroutable = rl.unroutable_count || 0;
        const rlDegraded = data.real.order_count > 0 && rlUnroutable > 0.05 * data.real.order_count;
        // Taux horaire net moyen par véhicule, calculé par le back sur TOUTES les tournées du jour
        // (taux = Σ gain_real / Σ (done−gone)) — indépendant des colonnes qu'on re-coûte ci-dessous.
        const rates = data.real_rates || {};
        const scooterRate = rates.scooter ? rates.scooter.rate : null;
        const carRate = rates.car ? rates.car.rate : null;
        const parisReal = paris ? paris.real : null;
        // Heures de chaque colonne — au même taux, c'est ce qui fait tout l'écart de coût :
        //   réel payé = heures RÉELLEMENT travaillées (done−gone : trafic, stationnement, aléas) ;
        //   réel-lib  = ce que la lib ESTIME pour ces mêmes tournées (routage optimal + service) ;
        //   simulé    = même estimation lib, sur les tournées que la lib aurait construites.
        // Δ méthode mesure donc l'écart théorie/exécution, Δ dispatch le seul effet du découpage.
        const hoursPaid = (m) => (m && m.elapsed_min != null ? m.elapsed_min / 60 : null);
        const hoursLib = (m) =>
          m && m.travel_min
            ? ((m.loading_min || 0) + m.travel_min + (m.brake_min || 0) + (m.wait_min || 0)) / 60
            : null;
        // Coût @ taux : heures Paris × taux scooter + heures hors-Paris × taux voiture.
        const mix = (hGlobal, hParis) => {
          if (hGlobal == null || (scooterRate == null && carRate == null)) return null;
          const ph = hParis || 0;
          return (scooterRate || 0) * ph + (carRate || 0) * Math.max(0, hGlobal - ph);
        };
        const rateLabel = [
          scooterRate != null ? `scooter ${r1(scooterRate)}€/h` : null,
          carRate != null ? `voiture ${r1(carRate)}€/h` : null,
        ].filter(Boolean).join(" · ");
        const globalCostRate = {
          label: rateLabel,
          paid: mix(hoursPaid(globalPaid), hoursPaid(parisReal)),
          lib: mix(hoursLib(globalLib), hoursLib(parisLib)),
          sim: mix(hoursLib(globalSim), hoursLib(paris ? paris.sim : null)),
          hoursPaid: hoursPaid(globalPaid),
          hoursLib: hoursLib(globalLib),
          hoursSim: hoursLib(globalSim),
        };
        const scooterCost = (h) => (scooterRate != null && h != null ? scooterRate * h : null);
        const parisCostRate = paris && {
          label: scooterRate != null ? `scooter ${r1(scooterRate)}€/h` : "",
          paid: scooterCost(hoursPaid(paris.real)),
          lib: scooterCost(hoursLib(parisLib)),
          sim: scooterCost(hoursLib(paris.sim)),
          hoursPaid: hoursPaid(paris.real),
          hoursLib: hoursLib(parisLib),
          hoursSim: hoursLib(paris.sim),
        };
        const docks = (data.docks || []).filter(
          (d) => (!parisOnly || d.zone === "paris") && (!dayOnly || d.in_day),
        );
        return (
          <>
            <div className="Sim-params" title="Paramètres ayant produit cette simulation.">
              <span><b>Jour</b> {data.day}</span>
              <span><b>Pas</b> {data.step_min} min</span>
              <span><b>Retard toléré</b> {data.max_delay_min} min</span>
              <span><b>Attente max</b> {data.max_wait_min} min</span>
              <span><b>Urgent si &lt;</b> {data.urgent_slack_min} min</span>
              <span title="Gain minimum visé par tournée pour cette simulation (scooter Paris / voiture banlieue) : comparé au versé, il guide la construction — le coût affiché reste celui du barème.">
                <b>Tournée visée</b> {data.min_tour_eur_paris == null && data.min_tour_eur_other == null
                  ? "désactivé"
                  : `${data.min_tour_eur_paris == null ? "—" : `${data.min_tour_eur_paris} €`} scooter · ${data.min_tour_eur_other == null ? "—" : `${data.min_tour_eur_other} €`} voiture`}
              </span>
              <span><b>Scooter</b> {data.max_bags_paris}s / {r1(data.max_weight_paris)}kg</span>
              <span><b>Voiture</b> {data.max_bags_other}s / {r1(data.max_weight_other)}kg</span>
              <span className="Sim-params-gen">générée {new Date(data.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            {rlDegraded && (
              <div className="Sim-warn">
                <b>Réel-lib peu fiable :</b> OSRM n’a pas su router {rlUnroutable} commande{rlUnroutable > 1 ? "s" : ""} sur {data.real.order_count} (matrice incomplète). Les colonnes <b>Réel (lib)</b> sous-estiment coût et temps — régénère la simulation quand l’OSRM est stable.
              </div>
            )}
            {parisOnlySim && (
              <div className="Sim-hint">
                <b>Simulation Paris intramuros</b> : le dispatch auto n’était activé que pour Paris ce
                jour-là. Le comparatif global n’est pas affiché — il opposerait des tournées Paris à un
                dispatch réel qui contient aussi la banlieue.
              </div>
            )}
            <div className="Sim-compare-row">
              {!parisOnlySim && (
                <CompareTable title={`Comparatif — ${data.day}${dayTag}`} paid={globalPaid} lib={globalLib} sim={globalSim} costRate={globalCostRate}
                  excesses={ex[dayOnly ? "day_only" : "all"]} />
              )}
              {paris && <CompareTable title={`dont Paris intramuros${dayTag}`} paid={paris.real} lib={parisLib} sim={paris.sim} costRate={parisCostRate}
                excesses={ex[dayOnly ? "paris_day_only" : "paris"]} />}
            </div>
            {data.excluded_count > 0 && (
              <div className="Sim-hint">
                <b>Population ISO</b> : {data.excluded_count} commande{data.excluded_count > 1 ? "s" : ""} non
                routable{data.excluded_count > 1 ? "s" : ""} (OSRM ne sait pas les placer) exclue
                {data.excluded_count > 1 ? "s" : ""} des trois colonnes — les trois comptent exactement la même
                population.
              </div>
            )}
            <div className="Sim-hint">
              <b>Réel payé</b> et <b>réel-lib</b> = les mêmes tournées, même exécution réelle (retard, avance et population
              identiques) ; seul le coût diffère (net livreur payé vs estimation de la lib). <b>Δ méthode</b> (réel-lib −
              réel payé) isole donc l’effet de la formule de coût — il est nul sur le retard et l’avance, réels des deux
              côtés. <b>Δ dispatch</b> (simulé − réel-lib) isole l’effet du re-découpage — la vraie valeur ajoutée de la
              lib. <b>Négatif = favorable</b> (vert) : le simulé réduit le coût, le retard ou l’attente.
            </div>

            <div className="Sim-cases">
              <div className="Sim-cases-col">
                <h4>Plus gros retards (réel)</h4>
                {((data.cases && data.cases.late) || []).map((c, i) => (
                  <div key={i} className="Sim-case">
                    <b>#{c.id}</b> · n° {c.ref} · créneau {c.slot} · livré {c.delivered}
                    <span className="Sim-late"> → retard {c.minutes} min</span>
                  </div>
                ))}
              </div>
              <div className="Sim-cases-col">
                <h4>Plus grosses avances (réel)</h4>
                {((data.cases && data.cases.early) || []).map((c, i) => (
                  <div key={i} className="Sim-case">
                    <b>#{c.id}</b> · n° {c.ref} · créneau {c.slot} · livré {c.delivered}
                    <span className="Sim-early"> → avance {c.minutes} min</span>
                  </div>
                ))}
              </div>
              <div className="Sim-cases-col">
                <h4>Re-livraisons (2+ passages)</h4>
                {((data.cases && data.cases.redeliveries) || []).map((c, i) => (
                  <div key={i} className="Sim-case">
                    <b>#{c.id}</b> · n° {c.ref} · créneau {c.slot} · 1er passage {c.first}, re-livré {c.delivered} ({c.passages}×)
                    {c.minutes > 0
                      ? <span className="Sim-late"> → retard {c.minutes} min</span>
                      : <span className="Sim-early"> → livré dans le créneau</span>}
                  </div>
                ))}
              </div>
            </div>

            <h3 className="Sim-tours-title">
              {docks.length} tournées{parisOnly ? " intramuros" : ""}{dayOnly ? " (journée seule)" : ""}
            </h3>
            <TargetRecap
              docks={docks}
              targetParis={data.min_tour_eur_paris}
              targetOther={data.min_tour_eur_other}
            />
            {(() => {
              const t = docks.reduce((a, d) => ({
                loading: a.loading + (d.loading_min || 0), travel: a.travel + (d.travel_min || 0),
                brake: a.brake + (d.brake_min || 0), wait: a.wait + (d.wait_min || 0),
              }), { loading: 0, travel: 0, brake: 0, wait: 0 });
              return (
                <div className="Sim-tours-totals" title="Cumul sur les tournées affichées.">
                  Total — chargement <b>{r1(t.loading)}</b>m · trajet pur <b>{r1(t.travel)}</b>m · coup de frein <b>{r1(t.brake)}</b>m · attente client <b>{r1(t.wait)}</b>m
                </div>
              );
            })()}
            <div className="Sim-tours">
              {docks.map((d) => (
                <SimDock key={d.label} dock={d} tasks={data.tasks} depot={data.depot}
                  avg={simAvg} />
              ))}
            </div>
          </>
        );
      })()}
    </div>
  );
}
SimulationPage.propTypes = { centerId: PropTypes.number };

export default connect((state) => ({ centerId: extractCenterId(state.user) }))(SimulationPage);
